import * as React from "react";
import clsx from "clsx";
import { Button } from "antd";
import { StepIndicator } from "@/components/ui/StepIndicator";
import Ring from "@/components/ui/Ring";
import { Step } from "@/Popups/contentSlide/components/Step";
import { DialogMessageType } from "@/types/commands";
import { useSlideEditStore } from "@/state/slideEditStore";
import { useNewPrecedentSlideStore } from "@/state/newPrecedentSlideStore";
import { isSlideEditFormattingFeaturesComplete } from "@/Popups/contentSlide/newPrecedentSlide/steps/formattingFeaturesConfig";
import SelectSlideStep from "./SelectSlideStep";
import SlideEditTypeRadioList from "./SlideEditTypeRadioList";
import SlideEditCustomizationDesignStep from "./SlideEditCustomizationDesignStep";
import SlideChartTimePeriodChat from "./SlideChartTimePeriodChat";
import SlideRelativeBenchmarksChat from "./SlideRelativeBenchmarksChat";
import SlideTextUpdateChat from "./SlideTextUpdateChat";
import {
  getOrderedSelectedEditTypes,
  isSlideEditCustomizationStep,
  SLIDE_EDIT_STEP_BAR_LABEL_BY_ID,
} from "../slideEditWizardConfig";

const FIXED_STEP_LABELS = ["Select Slide", "Edit Type"] as const;

const SlideEditMainContent: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const selectedSlideForEdit = useSlideEditStore((s) => s.selectedSlideForEdit);
  const selectedSlideEditTypeIds = useSlideEditStore((s) => s.selectedSlideEditTypeIds);
  const toggleSlideEditTypeId = useSlideEditStore((s) => s.toggleSlideEditTypeId);
  const clearSlideEditTypeSelection = useSlideEditStore((s) => s.clearSlideEditTypeSelection);
  const step0Complete = !!selectedSlideForEdit;
  const step1Complete = selectedSlideEditTypeIds.length > 0;

  const chartConfig = useNewPrecedentSlideStore((s) => s.chartConfig);
  const chartCount = useNewPrecedentSlideStore((s) => s.chartCount);
  const isKeyEventLayout = useNewPrecedentSlideStore((s) => s.isKeyEvent);

  const customizationFormattingComplete = React.useMemo(
    () => isSlideEditFormattingFeaturesComplete(chartCount, chartConfig, isKeyEventLayout),
    [chartCount, chartConfig, isKeyEventLayout]
  );

  const orderedEditSteps = React.useMemo(
    () => getOrderedSelectedEditTypes(selectedSlideEditTypeIds),
    [selectedSlideEditTypeIds]
  );

  const stepLabels = React.useMemo(() => {
    // Show a placeholder 3rd label until the user chooses edit types.
    if (orderedEditSteps.length === 0) return [...FIXED_STEP_LABELS, "Edit Specifications"];
    return [...FIXED_STEP_LABELS, ...orderedEditSteps.map((id) => SLIDE_EDIT_STEP_BAR_LABEL_BY_ID[id])];
  }, [orderedEditSteps]);

  const totalSteps = stepLabels.length;
  const questionsPerStep = React.useMemo(() => Array.from({ length: totalSteps }, () => 1), [totalSteps]);
  const stepSizes = React.useMemo(() => {
    const n = orderedEditSteps.length;
    if (n === 0) return [1, 1.15, 1];
    const dynamicSizes: number[] = orderedEditSteps.map((id) => (id === "chart-customization-options" ? 1.5 : 1));
    // keep the last step visually smaller, unless the last step is the customization UI
    if (dynamicSizes.length > 0 && orderedEditSteps[orderedEditSteps.length - 1] !== "chart-customization-options") {
      dynamicSizes[dynamicSizes.length - 1] = 0.5;
    }
    return [0.5, 0.5, ...dynamicSizes];
  }, [orderedEditSteps]);

  const [answersByStep, setAnswersByStep] = React.useState<number[]>(() =>
    Array.from({ length: Math.max(2, totalSteps) }, () => 0)
  );

  React.useEffect(() => {
    clearSlideEditTypeSelection();
  }, [selectedSlideForEdit?.slideId, clearSlideEditTypeSelection]);

  React.useEffect(() => {
    setAnswersByStep((prev) => {
      const next = Array.from({ length: totalSteps }, (_, i) => (i < prev.length ? prev[i] : 0));
      next[0] = step0Complete ? 1 : 0;
      next[1] = step1Complete ? 1 : 0;
      orderedEditSteps.forEach((editTypeId, i) => {
        const stepIndex = 2 + i;
        if (editTypeId === "chart-customization-options") {
          next[stepIndex] = customizationFormattingComplete ? 1 : 0;
        }
      });
      return next;
    });
  }, [totalSteps, step0Complete, step1Complete, orderedEditSteps, customizationFormattingComplete]);

  React.useEffect(() => {
    setCurrentStep((s) => {
      const max = Math.max(0, totalSteps - 1);
      return s > max ? max : s;
    });
  }, [totalSteps]);

  const onClose = React.useCallback(() => {
    try {
      Office.context.ui.messageParent(DialogMessageType.CLOSE);
    } catch {
      /* dialog host may be unavailable in dev */
    }
  }, []);

  const goNext = React.useCallback(() => {
    if (currentStep === 0 && !step0Complete) return;
    if (currentStep === 1 && !step1Complete) return;

    if (currentStep >= totalSteps - 1) {
      onClose();
      return;
    }
    setAnswersByStep((prev) => {
      const next = [...prev];
      if (currentStep < next.length) next[currentStep] = questionsPerStep[currentStep] ?? 1;
      return next;
    });
    setCurrentStep((s) => s + 1);
  }, [currentStep, onClose, step0Complete, step1Complete, totalSteps, questionsPerStep]);

  const goBack = React.useCallback(() => {
    if (currentStep <= 0) return;
    setCurrentStep((s) => s - 1);
  }, [currentStep]);

  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4 bg-gray-100">
      <div
        className={clsx(
          "w-full mx-auto py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={[...stepLabels]}
          stepSizes={stepSizes}
          labelClassName="text-xs leading-snug"
        />
      </div>

      <Ring className="grid h-full relative w-full min-h-[200px] mt-4">
        <Step show={currentStep === 0}>
          <SelectSlideStep />
        </Step>
        <Step show={currentStep === 1}>
          <SlideEditTypeRadioList value={selectedSlideEditTypeIds} onToggle={toggleSlideEditTypeId} />
        </Step>
        {orderedEditSteps.map((editTypeId, i) => {
          const stepIndex = 2 + i;
          return (
            <Step key={editTypeId} show={currentStep === stepIndex}>
              {isSlideEditCustomizationStep(editTypeId) ? (
                <SlideEditCustomizationDesignStep />
              ) : (
                <>
                  {editTypeId === "chart-time-period-price-refresh" ? <SlideChartTimePeriodChat /> : null}
                  {editTypeId === "chart-relative-benchmarks" ? <SlideRelativeBenchmarksChat /> : null}
                  {editTypeId === "text-on-slide" ? <SlideTextUpdateChat /> : null}
                </>
              )}
            </Step>
          );
        })}
      </Ring>

      <div className={`flex items-center w-full px-2 pl-10 pt-3 justify-end`}>
        <div className="flex gap-2">
          <Button danger onClick={onClose}>
            Cancel
          </Button>
          <Button type="default" onClick={goBack} disabled={currentStep === 0}>
            Back
          </Button>
          <Button
            type="primary"
            onClick={goNext}
            disabled={(currentStep === 0 && !step0Complete) || (currentStep === 1 && !step1Complete)}
          >
            {isLastStep ? "Finish" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SlideEditMainContent;
